<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, watch } from 'vue';
import { mmToPixels, pixelsToMm } from '@/utils/unitsUtils';
import { calculateFontSizesFromDimensions, calculateInitTextValues } from '@/utils/generalMathUtils';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInput, { type InputValue } from '@/components/shared/CInput';
import { useTransformClipboard } from '@/composables/useTransformClipboard';
import { useToaster } from '@/composables/toaster';
import type { CanvasEx } from '@/typings/canvas';
import type {
    ActiveSelection,
    BaseCanvasElement,
    BoundedTextElement,
    CanvasElement,
    CanvasOrSelection,
    ImagePlaceholderElement,
    PathITextElement,
    TextTransformElement
} from '@/typings/customilyElements/canvasElements';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import type { ElementsLink } from '@/utils/textMaskEffects';
import { ValueFormat } from '@/typings/designStudio/sourceElementTypes';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        canvasElement: CanvasOrSelection;
        editMode?: boolean;
        lockRatio?: boolean;
        /**
         * Editing width/height cascades to the other dimension keeping the current
         * aspect ratio of the bounding box (step 2 behavior)
         */
        linkedRatio?: boolean;
        bulkEdit?: boolean;
        units?: 'mm' | 'px';
        perspective?: boolean;
        skew?: boolean;
        /** Mask/cutout text-image links; bulk edits keep these pairs' relative rotation/size intact */
        elementsLinks?: ElementsLink[];
    }>(),
    {
        units: 'mm',
        elementsLinks: () => []
    }
);

const emit = defineEmits<{
    (e: 'arrange-layer', element: CanvasOrSelection, position: 'front' | 'forward' | 'back' | 'backward'): void;
}>();

const model = reactive({
    width: 0 as number | undefined,
    height: 0 as number | undefined,
    centerX: 0 as number | undefined,
    centerY: 0 as number | undefined,
    rotation: 0 as number | undefined,
    skewX: undefined as number | undefined,
    skewY: undefined as number | undefined,
    locked: false,
    isOverlay: false,
    perspectiveActive: false,
    perspectiveEnabled: false,
    hasInitWidth: false
});

const isActiveSelectionElement = (element: CanvasOrSelection): element is ActiveSelection<CanvasElement> =>
    element.type === 'activeSelection';

const isPathITextElement = (element: CanvasOrSelection): element is PathITextElement => element.type === 'path-itext';

const isBaseTextElement = (
    element: CanvasOrSelection
): element is BoundedTextElement | TextTransformElement | PathITextElement =>
    ['bounded-text', 'text-transform', 'path-itext'].includes(element.type ?? '');

const canvasDpi = computed(() => props.canvas.DPI);
const isBulkMode = computed(() => !!props.bulkEdit && props.canvasElement?.type === 'activeSelection');

const fromDisplayUnit = (px: number): number => {
    const value = props.units === 'mm' ? pixelsToMm(px, canvasDpi.value) : px;
    return Number(value.toFixed(2));
};
const toDisplayUnit = (value: number): number => {
    const result = props.units === 'mm' ? mmToPixels(value, canvasDpi.value) : value;
    return Number(result.toFixed(2));
};

/**
 * Representative type used only for single-value contexts (perspective gating, copy/paste
 * telemetry) where picking one member of a bulk selection is acceptable
 */
const elementType = computed(() => {
    const element = props.canvasElement;
    if (!element) return undefined;

    return isBulkMode.value && isActiveSelectionElement(element) ? element.getObjects()?.[0]?.type : element.type;
});

/**
 * Every object type present in the current selection (a single-item list outside bulk mode).
 * Bulk selections can mix element types, so UI gating must consider the whole set rather than
 * just the first object
 */
const selectionTypes = computed<string[]>(() => {
    const element = props.canvasElement;
    if (!element) return [];
    if (isBulkMode.value && isActiveSelectionElement(element)) {
        return element.getObjects().map((object) => object.type);
    }
    return [element.type];
});
const hasSelectionType = (type: string) => selectionTypes.value.includes(type);

const isTextTransform = computed(() => hasSelectionType('text-transform'));
const isPathIText = computed(() => hasSelectionType('path-itext'));
const isBoundedText = computed(() => hasSelectionType('bounded-text'));
// text-transform must never receive skew, even mixed into a bulk selection with bounded-text
const showSkew = computed(() => !isTextTransform.value && (isBoundedText.value || props.skew));
const showFlip = computed(() => !isPathIText.value && !isTextTransform.value);
// path-itext alone is never mask-capable, but a bulk selection can mix it with a mask pair
// that does need the reapply, so only skip the dispatch outside bulk mode
const needsTextMaskRefresh = computed(() => isBulkMode.value || !isPathIText.value);
const showPerspective = computed(
    () => props.perspective && !isBulkMode.value && elementType.value === 'image-placeholder'
);

const disabled = computed(() => ({
    common: model.locked,
    arrange: model.locked || model.isOverlay,
    flip: model.locked || isTextTransform.value
}));

const clipboard = useTransformClipboard();
const toast = useToaster();

const supportsCopyPaste = computed(() => !!props.canvasElement);

const canPasteDimensions = computed(
    () => supportsCopyPaste.value && !model.locked && !!clipboard.dimensionsFor(props.canvas)
);
const canPastePosition = computed(
    () => supportsCopyPaste.value && !model.locked && !!clipboard.positionFor(props.canvas)
);

watch(() => props.canvas, (canvas) => clipboard.bindCanvas(canvas), { immediate: true });

const placeholderFor = (value?: number) => (value === undefined ? 'Mixed' : '0');

/**
 * After a bulk apply, temporaryDiscardActiveObject recreates the ActiveSelection without firing
 * selection events, so the prop reference can be stale; prefer the current selection on the canvas
 */
const resolveElement = (): CanvasOrSelection | undefined => {
    if (!isBulkMode.value) return props.canvasElement;

    const active = props.canvas.getActiveObject() as CanvasOrSelection | null;
    if (active && isActiveSelectionElement(active)) return active;
};

// [sourceUuid (mask/cutout text), targetUuid (linked image)] pairs
const maskLinkUuidPairs = computed<Array<[string, string]>>(() =>
    props.elementsLinks
        .filter((link) => link.valueFormat === ValueFormat.TextAsMask || link.valueFormat === ValueFormat.TextAsCutout)
        .map((link) => [link.sourceId, link.targetId])
);

const getMaskLinkedPairs = (objects: CanvasElement[]): Array<[CanvasElement, CanvasElement]> => {
    const byUuid = new Map(objects.map((object) => [object.uuid as string, object]));
    const pairs: Array<[CanvasElement, CanvasElement]> = [];
    for (const [sourceId, targetId] of maskLinkUuidPairs.value) {
        const source = byUuid.get(sourceId);
        const target = byUuid.get(targetId);
        if (source && target) pairs.push([source, target]);
    }
    return pairs;
};

const uniformNumber = (values: number[]): number | undefined => {
    const [first] = values;
    return values.every((value) => value === first) ? first : undefined;
};

const transformPoint = (point: { x: number; y: number }, matrix: number[]) => ({
    x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
    y: matrix[1] * point.x + matrix[3] * point.y + matrix[5]
});

const getDimensionPx = (element: CanvasOrSelection, dimension: 'width' | 'height'): number => {
    if (isActiveSelectionElement(element)) {
        return dimension === 'width' ? element.getScaledWidth() : element.getScaledHeight();
    }
    if (isPathITextElement(element)) {
        return (dimension === 'width' ? element.horizontalDiameter : element.verticalDiameter) || 0;
    }
    return dimension === 'width' ? element.getWidth() : element.getHeight();
};

const syncDimensionFromElement = (dimension: 'width' | 'height') => {
    const element = resolveElement();
    if (!element) return;

    if (isBulkMode.value && isActiveSelectionElement(element)) {
        const pixels = uniformNumber(element.getObjects().map((object) => getDimensionPx(object, dimension)));
        model[dimension] = pixels === undefined ? undefined : fromDisplayUnit(pixels);
        return;
    }

    model[dimension] = fromDisplayUnit(getDimensionPx(element, dimension));
};

const loadBulkModel = (selection: ActiveSelection<CanvasElement>) => {
    const objects = selection.getObjects();
    const first = objects[0];
    if (!first) return;

    // Object coordinates are group-relative; compare and display them in absolute canvas space
    const groupMatrix = selection.calcTransformMatrix();
    const points = objects.map((object) => transformPoint({ x: object.left || 0, y: object.top || 0 }, groupMatrix));
    const centerX = uniformNumber(points.map((point) => point.x));
    const centerY = uniformNumber(points.map((point) => point.y));
    model.centerX = centerX === undefined ? undefined : fromDisplayUnit(centerX);
    model.centerY = centerY === undefined ? undefined : fromDisplayUnit(centerY);

    const angle = uniformNumber(objects.map((object) => object.angle || 0));
    model.rotation = angle === undefined ? undefined : angle + (selection.angle || 0);
    if (model.rotation) model.rotation = Number(model.rotation.toFixed(2))
    model.skewX = uniformNumber(objects.map((object) => object.skewX || 0));
    model.skewY = uniformNumber(objects.map((object) => object.skewY || 0));
    // If any selected object is locked/overlay, block edits for the whole batch, not just the first one
    model.locked = objects.some((object) => !!object.locked);
    model.isOverlay = objects.some((object) => !!object.isOverlay);
};

const loadModel = () => {
    const element = resolveElement();
    if (!element) return;

    syncDimensionFromElement('width');
    syncDimensionFromElement('height');
    model.rotation = Number(element.angle?.toFixed(2) || 0);

    const image = element.type === 'image-placeholder' ? element : undefined;
    model.perspectiveActive = !!image?.__perspectiveMode;
    model.perspectiveEnabled = !!image?.enablePerspective;
    model.hasInitWidth = !!image?.initWidth;

    if (isActiveSelectionElement(element)) {
        if (isBulkMode.value) {
            loadBulkModel(element);
            return;
        }

        const point = element.getPointByOrigin('center', 'center');
        model.centerX = fromDisplayUnit(point.x);
        model.centerY = fromDisplayUnit(point.y);
        model.skewX = model.skewY = undefined;
        model.locked = false;
        model.isOverlay = false;
        return;
    }

    model.centerX = fromDisplayUnit(element.left || 0);
    model.centerY = fromDisplayUnit(element.top || 0);
    model.skewX = element.skewX || 0;
    model.skewY = element.skewY || 0;
    model.locked = element.locked;
    model.isOverlay = element.isOverlay;
};

let internalUpdate = false;
const onElementModified = () => {
    // Skip model reloads triggered by our own writes so we don't clobber the value being typed
    if (internalUpdate) return;
    loadModel();
};

const attachListeners = (element: CanvasOrSelection) => {
    if (isBulkMode.value) {
        // The selection wrapper is recreated on every bulk apply; listen at canvas level instead
        props.canvas.on('object:modified', onElementModified);
        props.canvas.on('customilyActiveSelection:modified', onElementModified);
        return;
    }
    element.on('modified', onElementModified);
};

const detachListeners = (element?: CanvasOrSelection | null) => {
    element?.off('modified', onElementModified);
    props.canvas?.off('object:modified', onElementModified);
    props.canvas?.off('customilyActiveSelection:modified', onElementModified);
};

watch(
    () => props.canvasElement,
    (element, oldElement) => {
        detachListeners(oldElement);
        if (!element) return;
        attachListeners(element);
        loadModel();
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    detachListeners(props.canvasElement);
});

const applyUpdate = (
    updater: (element: CanvasOrSelection) => void,
    options: { resize?: boolean; textMask?: boolean; selectionSkip?: Record<string, boolean> } = {}
) => {
    const element = resolveElement();
    if (!element) return;

    internalUpdate = true;
    try {
        const isActiveSelection = isActiveSelectionElement(element);
        if (isBulkMode.value && isActiveSelection) {
            const objects = element.getObjects();
            const linkedPairs = getMaskLinkedPairs(objects);

            // Objects inside an ActiveSelection have group-relative coordinates; discard the
            // selection so updates operate on absolute values, then it gets recreated
            props.canvas.temporaryDiscardActiveObject(() => {
                // Snapshot post-discard (absolute coords) so deltas share a coordinate space with the post-update reading
                const before = new Map(objects.map((object) => [object, snapshotTransform(object)]));
                for (const object of objects) {
                    updater(object);
                    if (props.editMode && options.resize) {
                        object.editedSize = true;
                    }
                }
                // Keep mask-linked pairs rigid: apply the follower's change as a delta/ratio, not the same absolute value
                for (const [driver, follower] of linkedPairs) {
                    restoreLinkedPairRelation(driver, before.get(driver)!, follower, before.get(follower)!);
                    // Refresh so a follower shared by multiple drivers composes corrections instead of the last one winning
                    before.set(follower, snapshotTransform(follower));
                }
                for (const object of objects) {
                    object.setCoords();
                    object.fire('modified');
                }
            });
            props.canvas.requestRenderAll();
            if (options.textMask) {
                // Re-resolve: temporaryDiscardActiveObject replaced `element` with a new instance
                const activeSelection = resolveElement();
                if (activeSelection) props.canvas.fire('object:handle:textmask', { target: activeSelection });
            }
            // Lets the AngularJS side re-read canvas.getActiveObject() and refresh its group state
            props.canvas.fire('customilyActiveSelection:modified', options.selectionSkip ?? {});
            return;
        }

        updater(element);
        if (props.editMode && options.resize && !isActiveSelection) {
            element.editedSize = true;
        }
        element.setCoords();
        props.canvas.requestRenderAll();
        if (isActiveSelection && options.selectionSkip) {
            props.canvas.fire('customilyActiveSelection:modified', options.selectionSkip);
        }
        if (options.textMask) {
            props.canvas.fire('object:handle:textmask', { target: element });
        }
        // Keep the AngularJS side (TextService.loadTextData, current font size, etc.) in sync
        element.fire('modified');
    } finally {
        internalUpdate = false;
    }
};

const updateCenterX = (value: InputValue) => {
    const centerXValue = Number(value);
    model.centerX = centerXValue;
    applyUpdate(
        (element) => {
            const centerX = toDisplayUnit(centerXValue);
            if (isActiveSelectionElement(element)) {
                const point = element.getPointByOrigin('center', 'center');
                point.x = centerX;
                element.setPositionByOrigin(point, 'center', 'center');
                return;
            }
            const base: BaseCanvasElement = element;
            base.set('left', centerX);
        },
        { textMask: true, selectionSkip: { skipCenterX: true } }
    );
};

const updateCenterY = (value: InputValue) => {
    const centerYValue = Number(value);
    model.centerY = centerYValue;
    applyUpdate(
        (element) => {
            const centerY = toDisplayUnit(centerYValue);
            if (isActiveSelectionElement(element)) {
                const point = element.getPointByOrigin('center', 'center');
                point.y = centerY;
                element.setPositionByOrigin(point, 'center', 'center');
                return;
            }
            (element as BaseCanvasElement).set('top', centerY);
        },
        { textMask: true, selectionSkip: { skipCenterY: true } }
    );
};

const updateRotation = (value: InputValue) => {
    const rotation = Number(value);
    model.rotation = rotation;
    applyUpdate(
        (element) => {
            if (isActiveSelectionElement(element)) {
                element.rotate(rotation);
                return;
            }
            const base: BaseCanvasElement = element;
            if (base.setRotate) {
                base.setRotate(rotation);
                return;
            }
            base.set('angle', rotation);
        },
        { textMask: true, selectionSkip: { skipRotation: true } }
    );
};

const updateSkew = (axis: 'x' | 'y', value: InputValue) => {
    const degrees = Number(value);
    const key = axis === 'x' ? 'skewX' : 'skewY';
    model[key] = degrees;

    applyUpdate((element) => {
        // text-transform never supports skew, even when bulk-mixed with bounded-text (showSkew UI-gates this too)
        if (isActiveSelectionElement(element) || element.type === 'text-transform') return;
        const base: BaseCanvasElement = element;
        base.set(key, degrees);
    });
};

/**
 * Elements whose width/height setters already cascade the other dimension: lock-ratio images
 * (with init values) keep the aspect ratio inside setWidth/setHeight, and ActiveSelections are
 * lockUniScaling so they can only scale uniformly
 */
const cascadesDimensions = (element: CanvasOrSelection): boolean => {
    if (isActiveSelectionElement(element)) return true;
    const base: BaseCanvasElement = element;
    return !!props.lockRatio && base.type === 'image-placeholder' && !!base.hasInitValues?.();
};

const applyWidthPx = (element: CanvasOrSelection, widthPx: number, keepRatio = cascadesDimensions(element)) => {
    if (isActiveSelectionElement(element)) {
        // The static width of an ActiveSelection stays fixed while scaling; use it as reference
        element.scale(widthPx / (element.width || 1));
        return;
    }
    if (isPathITextElement(element)) {
        element.set('horizontalDiameter', widthPx);
        return;
    }

    const base: BaseCanvasElement = element;
    const width = widthPx / (base.scaleX || 1);
    if (base.setWidth) {
        base.setWidth(width, keepRatio);
        return;
    }
    base.set('width', width);
};

const applyHeightPx = (element: CanvasOrSelection, heightPx: number, keepRatio = cascadesDimensions(element)) => {
    if (isActiveSelectionElement(element)) {
        // The static height of an ActiveSelection stays fixed while scaling; use it as reference
        element.scale(heightPx / (element.height || 1));
        return;
    }
    if (isPathITextElement(element)) {
        element.set('verticalDiameter', heightPx);
        return;
    }

    const base: BaseCanvasElement = element;
    const height = heightPx / (base.scaleY || 1);
    if (base.setHeight) {
        base.setHeight(height, keepRatio);
        return;
    }
    base.set('height', height);
};

interface TransformSnapshot {
    left: number;
    top: number;
    angle: number;
    skewX: number;
    skewY: number;
    widthPx: number;
    heightPx: number;
}

const snapshotTransform = (object: CanvasElement): TransformSnapshot => ({
    left: object.left || 0,
    top: object.top || 0,
    angle: object.angle || 0,
    skewX: object.skewX || 0,
    skewY: object.skewY || 0,
    widthPx: getDimensionPx(object, 'width'),
    heightPx: getDimensionPx(object, 'height')
});

// Reapplies driver's position/angle/skew/size change to follower as a delta/ratio, preserving their prior offset
const restoreLinkedPairRelation = (
    driver: CanvasElement,
    driverBefore: TransformSnapshot,
    follower: CanvasElement,
    followerBefore: TransformSnapshot
) => {
    // Route through BaseCanvasElement - .set()'s per-type generic overloads don't unify across the CanvasElement union
    const followerBase: BaseCanvasElement = follower;

    const leftDelta = (driver.left || 0) - driverBefore.left;
    if (leftDelta) followerBase.set('left', followerBefore.left + leftDelta);

    const topDelta = (driver.top || 0) - driverBefore.top;
    if (topDelta) followerBase.set('top', followerBefore.top + topDelta);

    const angleDelta = (driver.angle || 0) - driverBefore.angle;
    if (angleDelta) followerBase.set('angle', followerBefore.angle + angleDelta);

    const skewXDelta = (driver.skewX || 0) - driverBefore.skewX;
    if (skewXDelta) followerBase.set('skewX', followerBefore.skewX + skewXDelta);

    const skewYDelta = (driver.skewY || 0) - driverBefore.skewY;
    if (skewYDelta) followerBase.set('skewY', followerBefore.skewY + skewYDelta);

    // keepRatio: false - width and height are both already derived from the driver's own ratios,
    // so the follower's lock-ratio cascade (which would recompute one from the other using its
    // own aspect ratio) must not override either value
    const widthRatio = driverBefore.widthPx ? getDimensionPx(driver, 'width') / driverBefore.widthPx : 1;
    if (widthRatio !== 1) applyWidthPx(follower, followerBefore.widthPx * widthRatio, false);

    const heightRatio = driverBefore.heightPx ? getDimensionPx(driver, 'height') / driverBefore.heightPx : 1;
    if (heightRatio !== 1) applyHeightPx(follower, followerBefore.heightPx * heightRatio, false);
};

const getElementRatio = (element: CanvasOrSelection): number => {
    const width = getDimensionPx(element, 'width');
    const height = getDimensionPx(element, 'height');
    return height > 0 ? width / height : 0;
};

/**
 * After a linked (proportional) resize, texts must rescale their font size limits and,
 * when user-editable, their initial position/size (step 2 behavior)
 */
const applyLinkedTextAdjustments = (element: CanvasOrSelection) => {
    if (!isBaseTextElement(element) || !element.originalWidth) return;

    const sizes = calculateFontSizesFromDimensions(
        element.getWidth(),
        element.originalWidth,
        element.originalMinSizePx ?? 0,
        element.originalMaxSizePx ?? 0
    );
    element.setMinSizePx?.(sizes.min);
    element.setMaxSizePx?.(sizes.max);

    if (element.allowEdit && element.originalInitCenterX != null) {
        const values = calculateInitTextValues(
            element.getWidth(),
            element.originalWidth,
            element.originalInitCenterX,
            element.originalInitCenterY ?? 0,
            element.originalInitFontSize ?? 0
        );
        element.setInitPositionAndSize?.(values.initCenterX, values.initCenterY, values.initFontSize);
    }
};

const updateWidth = (value: InputValue) => {
    const widthValue = Number(value);
    model.width = widthValue;
    applyUpdate(
        (element) => {
            const widthPx = toDisplayUnit(widthValue);
            // The ratio must be captured before resizing
            const ratio = getElementRatio(element);
            applyWidthPx(element, widthPx);

            if (props.linkedRatio && ratio && !isActiveSelectionElement(element)) {
                applyHeightPx(element, widthPx / ratio);
                applyLinkedTextAdjustments(element);
            }
        },
        { resize: true, textMask: needsTextMaskRefresh.value, selectionSkip: { skipWidth: true } }
    );
    syncDimensionFromElement('height');
};

const updateHeight = (value: InputValue) => {
    const heightValue = Number(value);
    model.height = heightValue;
    applyUpdate(
        (element) => {
            const heightPx = toDisplayUnit(heightValue);
            // The ratio must be captured before resizing
            const ratio = getElementRatio(element);
            applyHeightPx(element, heightPx);

            if (props.linkedRatio && ratio && !isActiveSelectionElement(element)) {
                applyWidthPx(element, heightPx * ratio);
                applyLinkedTextAdjustments(element);
            }
        },
        { resize: true, textMask: needsTextMaskRefresh.value, selectionSkip: { skipHeight: true } }
    );
    syncDimensionFromElement('width');
};

const onCopyDimensions = () => {
    if (!supportsCopyPaste.value) return;
    const widthPx = model.width && toDisplayUnit(model.width);
    const heightPx = model.height && toDisplayUnit(model.height);
    if (widthPx === undefined && heightPx === undefined) {
        toast.show('No shared dimensions to copy');
        return;
    }
    clipboard.copyDimensions(props.canvas, { widthPx, heightPx, sourceType: elementType.value });
    fireDatadogLog(DatadogEvent.CopyDsDimensions);
    toast.show('Dimensions copied');
};

const onCopyPosition = () => {
    if (!supportsCopyPaste.value) return;
    // rotation/skew are already in degrees, only the center needs px conversion
    const centerXPx = model.centerX && toDisplayUnit(model.centerX);
    const centerYPx = model.centerY && toDisplayUnit(model.centerY);
    const { rotation, skewX, skewY } = model;
    if (
        centerXPx === undefined &&
        centerYPx === undefined &&
        rotation === undefined &&
        skewX === undefined &&
        skewY === undefined
    ) {
        toast.show('No shared position to copy');
        return;
    }
    clipboard.copyPosition(props.canvas, { centerXPx, centerYPx, rotation, skewX, skewY, sourceType: elementType.value });
    fireDatadogLog(DatadogEvent.CopyDsPosition);
    toast.show('Position copied');
};

const onPasteDimensions = () => {
    const element = resolveElement();
    const clip = clipboard.dimensionsFor(props.canvas);
    if (!element || !clip) return;
    const { widthPx, heightPx } = clip;
    applyUpdate(
        (target) => {
            // Capture the target's ratio before resizing so linked/locked-ratio targets keep their own proportions
            const ratio = getElementRatio(target);
            const linked = props.linkedRatio && ratio > 0 && !isActiveSelectionElement(target);

            if (widthPx !== undefined) {
                applyWidthPx(target, widthPx);
                if (linked) {
                    applyHeightPx(target, widthPx / ratio);
                    applyLinkedTextAdjustments(target);
                } else if (heightPx !== undefined && !cascadesDimensions(target)) {
                    // Lock-ratio images and selections already cascaded height inside applyWidthPx;
                    // others take the copied height literally
                    applyHeightPx(target, heightPx);
                }
            } else if (heightPx !== undefined) {
                applyHeightPx(target, heightPx);
                if (linked) {
                    applyWidthPx(target, heightPx * ratio);
                    applyLinkedTextAdjustments(target);
                }
            }
        },
        { resize: true, textMask: needsTextMaskRefresh.value }
    );
    syncDimensionFromElement('width');
    syncDimensionFromElement('height');
    fireDatadogLog(DatadogEvent.PasteDsDimensions);
    toast.show('Dimensions pasted');
};

const onPastePosition = () => {
    const element = resolveElement();
    const clip = clipboard.positionFor(props.canvas);
    if (!element || !clip) return;
    if (clip.centerXPx !== undefined) updateCenterX(fromDisplayUnit(clip.centerXPx));
    if (clip.centerYPx !== undefined) updateCenterY(fromDisplayUnit(clip.centerYPx));
    if (clip.rotation !== undefined) updateRotation(clip.rotation);
    if (showSkew.value) {
        if (clip.skewX !== undefined) updateSkew('x', clip.skewX);
        if (clip.skewY !== undefined) updateSkew('y', clip.skewY);
    }
    fireDatadogLog(DatadogEvent.PasteDsPosition);
    toast.show('Position pasted');
};

const flipElement = (axis: 'x' | 'y' = 'x') => {
    const element = props.canvasElement;
    if (!element) return;

    const objects = isActiveSelectionElement(element) ? element.getObjects() : [element];

    for (const object of objects) {
        const flip = object.getFlip();
        const flipX = axis == 'x' ? !flip.flipX : flip.flipX;
        const flipY = axis == 'y' ? !flip.flipY : flip.flipY;

        object.setFlip(flipX!, flipY!);
        object.setCoords();
    }

    props.canvas.requestRenderAll();
};

const arrangeElement = (position: 'front' | 'forward' | 'back' | 'backward') => {
    const element = props.canvasElement;
    if (!element) return;
    emit('arrange-layer', element, position);
};

const togglePerspective = () => {
    const element = resolveElement() as ImagePlaceholderElement;
    if (!element?.togglePerspective) return;

    element.enablePerspective = true;
    element.togglePerspective(!element.__perspectiveMode);
    model.perspectiveActive = !!element.__perspectiveMode;
    model.perspectiveEnabled = true;
};

const resetPerspective = () => {
    const element = resolveElement() as ImagePlaceholderElement;
    if (!element?._removePerspective) return;

    element._removePerspective();
    model.perspectiveActive = !!element.__perspectiveMode;
    model.perspectiveEnabled = false;
};
</script>

<template>
    <div class="element-transform-wrapper">
        <section>
            <div class="d-flex align-items-center">
                <h6>Dimensions</h6>
                <c-button
                    v-tippy
                    content="Copy Dimensions"
                    class="ml-auto mr-1"
                    size="icon"
                    :icon="['far', 'copy']"
                    :disabled="!supportsCopyPaste"
                    @click="onCopyDimensions"
                />
                <c-button
                    v-tippy
                    content="Paste Dimensions"
                    size="icon"
                    :icon="['far', 'paste']"
                    :disabled="!canPasteDimensions"
                    @click="onPasteDimensions"
                />
            </div>
            <div class="border-bottom" />
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.width"
                    :label="`Width (${units})`"
                    name="width"
                    type="decimal"
                    :placeholder="placeholderFor(model.width)"
                    :min="1"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateWidth"
                />
                <font-awesome-icon v-if="linkedRatio" class="mt-3" :icon="['far', 'link']" />
                <c-input
                    class="flex-fill"
                    :value="model.height"
                    :label="`Height (${units})`"
                    type="decimal"
                    name="height"
                    :placeholder="placeholderFor(model.height)"
                    :min="1"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateHeight"
                />
            </div>
        </section>
        <section>
            <div class="d-flex align-items-center">
                <h6>Position</h6>
                <c-button
                    v-tippy
                    content="Copy Position"
                    class="ml-auto mr-1"
                    size="icon"
                    :icon="['far', 'copy']"
                    :disabled="!supportsCopyPaste"
                    @click="onCopyPosition"
                />
                <c-button
                    v-tippy
                    content="Paste Position"
                    size="icon"
                    :icon="['far', 'paste']"
                    :disabled="!canPastePosition"
                    @click="onPastePosition"
                />
            </div>
            <div class="border-bottom" />
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.centerX"
                    :label="`Position x (${units})`"
                    type="decimal"
                    name="centerX"
                    :placeholder="placeholderFor(model.centerX)"
                    :min="-9999999"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateCenterX"
                />
                <c-input
                    class="flex-fill"
                    :value="model.centerY"
                    :label="`Position y (${units})`"
                    type="decimal"
                    name="centerY"
                    :placeholder="placeholderFor(model.centerY)"
                    :min="-9999999"
                    :max="9999999"
                    :disabled="disabled.common"
                    @input="updateCenterY"
                />
            </div>
            <div class="input-container">
                <c-input
                    class="flex-fill"
                    :value="model.rotation"
                    label="Rotation (dg)"
                    type="decimal"
                    name="rotation"
                    :placeholder="placeholderFor(model.rotation)"
                    :min="-360"
                    :max="360"
                    :disabled="disabled.common"
                    @input="updateRotation"
                />
                <template v-if="showSkew">
                    <c-input
                        class="flex-fill"
                        :value="model.skewX"
                        label="Skew Horizontal (dg)"
                        type="decimal"
                        name="skewx"
                        :placeholder="placeholderFor(model.skewX)"
                        :min="-80"
                        :max="80"
                        :disabled="disabled.common"
                        @input="updateSkew('x', $event)"
                    />
                    <c-input
                        class="flex-fill"
                        :value="model.skewY"
                        label="Skew vertical (dg)"
                        type="decimal"
                        name="skewy"
                        :placeholder="placeholderFor(model.skewY)"
                        :min="-80"
                        :max="80"
                        :disabled="disabled.common"
                        @input="updateSkew('y', $event)"
                    />
                </template>
            </div>
        </section>
        <section class="flex-row">
            <div>
                <label class="m-0">Arrange Layers</label>
                <div class="input-container">
                    <c-button
                        v-tippy
                        content="Bring to front"
                        size="icon"
                        :icon="['far', 'bring-front']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('front')"
                    />
                    <c-button
                        v-tippy
                        content="Bring forward"
                        size="icon"
                        :icon="['far', 'bring-forward']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('forward')"
                    />
                    <c-button
                        v-tippy
                        content="Send backward"
                        size="icon"
                        :icon="['far', 'send-backward']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('backward')"
                    />
                    <c-button
                        v-tippy
                        content="Send to back"
                        size="icon"
                        :icon="['far', 'send-back']"
                        :disabled="disabled.arrange"
                        @click="arrangeElement('back')"
                    />
                </div>
            </div>
            <template v-if="showFlip">
                <div class="border-left" />
                <div>
                    <label class="m-0">Flip</label>
                    <div class="input-container">
                        <c-button
                            v-tippy
                            content="Flip Horizontal"
                            size="icon"
                            :icon="['far', 'reflect-horizontal']"
                            :disabled="disabled.flip"
                            @click="flipElement('x')"
                        />
                        <c-button
                            v-tippy
                            content="Flip Vertial"
                            size="icon"
                            :icon="['far', 'reflect-vertical']"
                            :disabled="disabled.flip"
                            @click="flipElement('y')"
                        />
                    </div>
                </div>
            </template>
            <div v-if="showPerspective" class="align-self-end">
                <c-button
                    text="Perspective"
                    :variant="model.perspectiveActive ? 'outline-primary' : 'light'"
                    :icon="['fak', 'perspective']"
                    :disabled="model.hasInitWidth"
                    @click="togglePerspective"
                />
                <c-button
                    v-if="model.perspectiveEnabled"
                    v-tippy
                    content="Reset perspective"
                    class="ml-2"
                    variant="light"
                    size="icon"
                    :icon="['fas', 'rotate-left']"
                    @click="resetPerspective"
                />
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.element-transform-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin: 0;
        }
        .input-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            .c-input-wrapper {
                flex: 1;
            }
        }
    }
}
</style>
